import { useEffect, useState } from 'react';
import { useSubscriptionOverride } from '@/hooks/useSubscriptionOverride';
import { useSubscriptionPlans } from '@/hooks/useTenantSubscription';
import { ModalPortal } from '@components/common/ModalPortal';
import { SearchableSelect } from '@components/common/SearchableSelect';
import type { CurrentSubscription } from '@/types/subscriptions';
import type { SubscriptionOverridePayload } from '@/types/promote';
import { EntitlementOverrideEditor } from './EntitlementOverrideEditor';

type EditSubscriptionOverridesDrawerProps = {
  accountId: string;
  tenantId?: string;
  open: boolean;
  onClose: () => void;
  initial: CurrentSubscription;
};

const BILLING_MODE_OPTIONS = [
  { id: 'card', label: 'Card' },
  { id: 'invoice', label: 'Invoice' },
];

export function EditSubscriptionOverridesDrawer({
  accountId,
  tenantId,
  open,
  onClose,
  initial,
}: EditSubscriptionOverridesDrawerProps) {
  const update = useSubscriptionOverride(accountId, tenantId);
  const plans = useSubscriptionPlans();
  const [planName, setPlanName] = useState(initial.plan.name);
  const [billingMode, setBillingMode] = useState(initial.billing_mode);
  const [customAmount, setCustomAmount] = useState('');
  const [customAmountTouched, setCustomAmountTouched] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [trialEndsAtTouched, setTrialEndsAtTouched] = useState(false);
  const [contractNotes, setContractNotes] = useState('');
  const [contractNotesTouched, setContractNotesTouched] = useState(false);
  const [featureOverrides, setFeatureOverrides] = useState<Record<string, unknown>>({});
  const [limitOverrides, setLimitOverrides] = useState<Record<string, number | null>>({});

  useEffect(() => {
    if (!open) {
      return;
    }
    update.reset();
    setPlanName(initial.plan.name);
    setBillingMode(initial.billing_mode);
    setCustomAmount(
      initial.overrides?.custom_amount_cents != null
        ? String(initial.overrides.custom_amount_cents)
        : '',
    );
    setCustomAmountTouched(false);
    setTrialEndsAt(initial.trial_ends_at ? initial.trial_ends_at.slice(0, 10) : '');
    setTrialEndsAtTouched(false);
    setContractNotes(initial.overrides?.contract_notes ?? '');
    setContractNotesTouched(false);
    setFeatureOverrides(initial.overrides?.feature_overrides ?? {});
    setLimitOverrides(initial.overrides?.limit_overrides ?? {});
  }, [initial, open]);

  if (!open) {
    return null;
  }

  async function submit() {
    const payload: SubscriptionOverridePayload = {
      plan_name: planName as SubscriptionOverridePayload['plan_name'],
      billing_mode: billingMode,
      feature_overrides: featureOverrides,
      limit_overrides: limitOverrides,
    };
    if (customAmountTouched) {
      payload.custom_amount_cents = customAmount.trim() ? Number(customAmount) : null;
    }
    if (trialEndsAtTouched) {
      payload.trial_ends_at = trialEndsAt ? new Date(`${trialEndsAt}T23:59:59Z`).toISOString() : null;
    }
    if (contractNotesTouched) {
      payload.contract_notes = contractNotes.trim() ? contractNotes.trim() : null;
    }

    await update.mutateAsync(payload);
    onClose();
  }

  const mutationError = update.error instanceof Error ? update.error.message : null;

  return (
    <ModalPortal>
      <div className="apollo-modal-backdrop" role="presentation" onClick={onClose}>
        <div
          className="apollo-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Edit subscription"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="detail-stack">
            <div>
              <p className="entity-breadcrumb">Subscription</p>
              <h2>Edit subscription</h2>
            </div>

            <div className="form-grid">
              <label className="form-field">
                <span>Plan</span>
                <SearchableSelect
                  options={
                    plans.data?.length
                      ? plans.data.map((plan) => ({ id: plan.name, label: plan.display_name }))
                      : [{ id: initial.plan.name, label: initial.plan.display_name }]
                  }
                  value={planName}
                  onChange={setPlanName}
                  disabled={plans.isLoading}
                  isLoading={plans.isLoading}
                  placeholder="Select plan"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>Billing mode</span>
                <SearchableSelect
                  options={BILLING_MODE_OPTIONS}
                  value={billingMode}
                  onChange={(value) => setBillingMode(value as typeof billingMode)}
                  placeholder="Billing mode"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>Custom monthly amount (cents)</span>
                <input
                  type="number"
                  value={customAmount}
                  placeholder={
                    initial.entitlements.amount_cents != null
                      ? String(initial.entitlements.amount_cents)
                      : undefined
                  }
                  onChange={(event) => {
                    setCustomAmount(event.target.value);
                    setCustomAmountTouched(true);
                  }}
                />
              </label>
              <label className="form-field">
                <span>Trial ends</span>
                <input
                  type="date"
                  value={trialEndsAt}
                  onChange={(event) => {
                    setTrialEndsAt(event.target.value);
                    setTrialEndsAtTouched(true);
                  }}
                />
              </label>
              <label className="form-field form-field-wide">
                <span>Contract notes</span>
                <textarea
                  rows={3}
                  value={contractNotes}
                  onChange={(event) => {
                    setContractNotes(event.target.value);
                    setContractNotesTouched(true);
                  }}
                />
              </label>
              <div className="form-field-wide">
                <EntitlementOverrideEditor
                  featureOverrides={featureOverrides}
                  limitOverrides={limitOverrides}
                  onFeatureOverridesChange={setFeatureOverrides}
                  onLimitOverridesChange={setLimitOverrides}
                />
              </div>
            </div>

            {mutationError ? (
              <div className="form-error">{mutationError}</div>
            ) : null}

            <div className="entity-header-actions">
              <button type="button" className="button button-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={submit}
                disabled={update.isPending}
              >
                {update.isPending ? 'Saving...' : 'Save subscription'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
